import { useEffect, useState } from "react";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesExp from "../../../styles/SearchExpensePopover.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import ProjectSelect from "Dashboard/scenes/Projects/components/ProjectSelect";
import { getExpensesList, setSearchExpensePopover } from "../main.slice";
import { SearchType } from "Dashboard/domain/SearchType";
import { IExpenseSearch } from "Dashboard/scenes/Expenses/domain/IExpenseSearch";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IExpenseSearch>;
  register: UseFormRegister<IExpenseSearch>;
  control: Control<IExpenseSearch, any>;
  reset: UseFormReset<IExpenseSearch>;
  setFocus: UseFormSetFocus<IExpenseSearch>;
  getValues: UseFormGetValues<IExpenseSearch>;
  setValue: UseFormSetValue<IExpenseSearch>;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
};

const SearchExpensePopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { register, handleSubmit, control, getValues, setValue, setFocus } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const onSubmit = async (data: IExpenseSearch) => {
    try {
      close();
      dispatch(getExpensesList(data));
    } catch (err) {
      console.error(err);
    }
  };

  const close = () => {
    dispatch(setSearchExpensePopover({ isOpen: false }));
  };

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchType(searchType);
  };

  useEffect(() => {
    if (props.searchType === SearchType.BASIC) {
      setFocus("searchTerm")
    } else {
      setFocus("title");
    }
  }, [setFocus, props.searchType]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`${classesExp.searchPopover} ${classes.basicPopover}`}
    >
      {props.searchType === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={classes.col}>
              <input
                type="text"
                placeholder=""
                className={`${classes.itemBorder} ${classes.fullWidth}`}
                {...register("searchTerm")}
              />
              <div className={classes.gPopoverDisclaimer}>
                * možete uneti više reči tj. kriterijuma pretrage
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}

      {props.searchType === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naziv</label>
              <input type="text" className={classes.itemBorder}
                {...register("title")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>projekat</label>
              <div className={classesExp.dropdownContainer}>
                <Controller
                  name="projectId"
                  control={control}
                  defaultValue={undefined}
                  render={({ field: { onChange } }) => (
                    <ProjectSelect
                      value={getValues("projectId")}
                      onChange={onChange}
                      customStyles={styles.customStyleClassic}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>iznos od</label>
              <input type="text" className={classes.itemBorder}
                {...register("amountFrom")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>iznos do</label>
              <input type="text" className={classes.itemBorder}
                {...register("amountTo")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>

              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchExpensePopover;
